import {ServiceBase} from "../ServiceBase";
import {
  ACTIONS,
  COMPONENT_NAMES,
  ICallToActionParams,
  IFullSafezoneHandler,
  IMiniSafezoneHandler,
  ISafezoneService,
  OPEN_SAFEZONE_SOURCE_VALUES,
} from "./safezone.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {ICreateSafezoneObjectParams, ISafezoneObject} from "./safezone-object-factory.service.interface";
import {action, observable} from "mobx";
import {Check} from "@sparkware/app-utils";

export class SafezoneService extends ServiceBase implements ISafezoneService{

  @observable.ref
  hasError:boolean = false;

  private _safezoneObj:ISafezoneObject = null;
  private _fullSafezoneHandler: IFullSafezoneHandler = null;
  private _miniSafezoneHandler: IMiniSafezoneHandler = null;

  constructor(services: IServiceFactoryExtended) {
    super(services);

    this.reaction(
      () => this.services.user.isAuthorized,
      (isAuthorized) => {
        if (isAuthorized) {
          if (this.services.abTestingFeatureDataProvider.enableSafeZone) {
            this._connectToSafezone();
          }
        } else {
          this._logoutSafezone()
        }},
        {
          fireImmediately: true
        }
    );
  }

  get isEnabled(): boolean {
    if(this.services.user.isAuthorized) {
      return this.services.abTestingFeatureDataProvider.enableSafeZone;
    } else {
      return false;
    }
  }

  @action.bound
  private _setHasError(value: boolean) {
    this.hasError = value;
  }

  private async _connectToSafezone():Promise<void>{
    this._fullSafezoneHandler = this.services.safezoneObjectFactory.createFullSafezoneHandler();
    this._miniSafezoneHandler = this.services.safezoneObjectFactory.createMiniSafezoneHandler();

    this._setHasError(false);
    const createSafezoneObjectParams:ICreateSafezoneObjectParams = {
      onCallToActionCallback: this._onCallToActionFunc,
      onReadyCallback: this._onSafezoneReady
    };
    try {
      this._safezoneObj = await this.services.safezoneObjectFactory.createSafezoneObject(createSafezoneObjectParams);
      this.services.analyticsDataLayer.pushSafezoneInitParams();
      if(this._safezoneObj.full){
        this._fullSafezoneHandler.init(this._safezoneObj.full);
      } else {
        this.services.logger.error("Full Safezone object wasn't created!");
      }
      if(this._safezoneObj.mini){
        this._miniSafezoneHandler.init(this._safezoneObj.mini);
      } else {
        this.services.logger.error("Mini Safezone object wasn't created!");
      }
    } catch (err) {
      this._setHasError(true);
      this.services.logger.error("Safezone object wasn't created!", err);
    }
  }

  public async reconnectToSafezone(): Promise<void> {
    if(this.hasError) {
     await this._connectToSafezone();
    }
  }

  private _onSafezoneReady = (component: COMPONENT_NAMES) => {
    if(!this.services.user.isAuthorized) {
      return; // This might happen especially in automation when you do very fast login logout. In this case this callback is called by safezone after the user is logged out
    }
    switch(component){
      case (COMPONENT_NAMES.MINI):
        this._miniSafezoneHandler?.onReady();
        break;
      case(COMPONENT_NAMES.FULL):
        this._fullSafezoneHandler?.onReady();
        break;
    }
  };

  private _onCallToActionFunc = (params:ICallToActionParams) => {
    switch(params.actionId){
      case (ACTIONS.OPEN_FULL):
        this._fullSafezoneHandler.openFullSafezone(OPEN_SAFEZONE_SOURCE_VALUES.USER_AREA, this._miniSafezoneHandler.correlationID);
        this._miniSafezoneHandler.closeMiniSafezone();
        break;
      case (ACTIONS.CLOSE_FULL):
        this._fullSafezoneHandler.closedFromSafezone = true;
        //TODO call drawer.back() when implement user menu in mobile
        this.services.drawer.right.closeDrawer();
        break;
      case (ACTIONS.OPEN_EXTERNAL_LINK):
        this.services.externalLinks.openLink({
          sourceLink: params.url,
          view: 'window'
        });
        break;
      case (ACTIONS.OPEN_RESPONSIBLE_GAMING):
        this.services.externalLinks.openResponsibleGamblingLink();
        break;
      case (ACTIONS.OPEN_CONTACT_US):
        this.services.externalLinks.openContactUs({view: 'window'});
        break;
      case (ACTIONS.LOG_OUT):
        this.services.user.logout();
        break;
      case (ACTIONS.KICK_OUT): 
        this._kickUserAfterSelfExclude();
        break;
    }
    this.services.logger.info("SafeZone call to action: " + JSON.stringify(params));
  };

  public openFullSafezone(): void {
    if(this._fullSafezoneHandler){
      this._fullSafezoneHandler.openFullSafezone(OPEN_SAFEZONE_SOURCE_VALUES.LOBBY);
    }
  }

  private _logoutSafezone(){
    if (!Check.isNullOrUndefined(this._safezoneObj)){
      this._miniSafezoneHandler.resetOnLogout();
      this._fullSafezoneHandler.resetOnLogout();
      this._safezoneObj.logout();
      this._safezoneObj = null;
      this._fullSafezoneHandler = null;
      this._miniSafezoneHandler = null;
    }
  }

  private _kickUserAfterSelfExclude() {
    this.services.user.logout();

    let structuredLoger = this.services.sdk.structuredLoggingService
      .getLogger()
      .addCategory("SAFEZONE TRACKING")
      .addAction("KickOutOfClient")
    structuredLoger.info();
  }
}